import React, { useState } from 'react';
import { ExternalLink } from 'react-feather';
import { useTranslations } from 'use-intl';
import { PlanAData, SUPABASE_BUCKET } from '@/pages/CustomerSupport/types';
import StarRating from '@/stories/Components/StarRating/StarRating';
import { formatCurrency } from '@/utils/utils';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';

interface PlanADataSectionProps {
	planAData: PlanAData;
}

const PlanADataSection: React.FC<PlanADataSectionProps> = ({ planAData }) => {
	const t = useTranslations('SupportClients');
	const supabase = useSupabaseStore((state) => state.supabase);

	const [platformScoreSignedUrl, setPlatformScoreSignedUrl] = useState<string | null>(null);
	const [completionRateSignedUrl, setCompletionRateSignedUrl] = useState<string | null>(null);
	const [weeklySummarySignedUrls, setWeeklySummarySignedUrls] = useState<Record<number, string>>(
		{}
	);
	const [loadingPlatformScore, setLoadingPlatformScore] = useState(false);
	const [loadingCompletionRate, setLoadingCompletionRate] = useState(false);
	const [loadingWeeklySummaries, setLoadingWeeklySummaries] = useState<Record<number, boolean>>({});

	const handlePlatformScoreClick = async () => {
		if (!planAData.platform_score_image_url) return;

		if (platformScoreSignedUrl) {
			window.open(platformScoreSignedUrl, '_blank');
			return;
		}

		setLoadingPlatformScore(true);

		try {
			const pathMatch = planAData.platform_score_image_url.match(
				`/${SUPABASE_BUCKET.WHATSAPP_IMAGES}/(.*)`
			);
			if (!pathMatch) {
				throw new Error('Invalid storage URL format');
			}

			const path = pathMatch[1];
			const { data, error } = await supabase.storage
				.from(SUPABASE_BUCKET.WHATSAPP_IMAGES)
				.createSignedUrl(path, 3600);

			if (error) throw error;

			setPlatformScoreSignedUrl(data.signedUrl);
			window.open(data.signedUrl, '_blank');
		} catch (err) {
			console.error('Error getting signed URL:', err);
		} finally {
			setLoadingPlatformScore(false);
		}
	};

	const handleCompletionRateClick = async () => {
		if (!planAData.completion_rate_image_url) return;

		if (completionRateSignedUrl) {
			window.open(completionRateSignedUrl, '_blank');
			return;
		}

		setLoadingCompletionRate(true);

		try {
			const pathMatch = planAData.completion_rate_image_url.match(
				`/${SUPABASE_BUCKET.WHATSAPP_IMAGES}/(.*)`
			);
			if (!pathMatch) {
				throw new Error('Invalid storage URL format');
			}

			const path = pathMatch[1];
			const { data, error } = await supabase.storage
				.from(SUPABASE_BUCKET.WHATSAPP_IMAGES)
				.createSignedUrl(path, 3600);

			if (error) throw error;

			setCompletionRateSignedUrl(data.signedUrl);
			window.open(data.signedUrl, '_blank');
		} catch (err) {
			console.error('Error getting signed URL:', err);
		} finally {
			setLoadingCompletionRate(false);
		}
	};

	const handleWeeklySummaryClick = async (imageUrl: string, index: number) => {
		if (weeklySummarySignedUrls[index]) {
			window.open(weeklySummarySignedUrls[index], '_blank');
			return;
		}

		setLoadingWeeklySummaries((prev) => ({ ...prev, [index]: true }));

		try {
			const pathMatch = imageUrl.match(`/${SUPABASE_BUCKET.WHATSAPP_IMAGES}/(.*)`);
			if (!pathMatch) {
				throw new Error('Invalid storage URL format');
			}

			const path = pathMatch[1];
			const { data, error } = await supabase.storage
				.from(SUPABASE_BUCKET.WHATSAPP_IMAGES)
				.createSignedUrl(path, 3600);

			if (error) throw error;

			setWeeklySummarySignedUrls((prev) => ({ ...prev, [index]: data.signedUrl }));
			window.open(data.signedUrl, '_blank');
		} catch (err) {
			console.error('Error getting signed URL:', err);
		} finally {
			setLoadingWeeklySummaries((prev) => ({ ...prev, [index]: false }));
		}
	};

	return (
		<div className="border-t pt-4">
			<h3 className="text-md font-semibold mb-3">{t('PlanAData')}</h3>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
				{planAData.monthly_income !== undefined && (
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('MonthlyIncome')}</label>
						<p className="text-sm font-medium">{formatCurrency(planAData.monthly_income)}</p>
					</div>
				)}
				{planAData.monthly_drives !== undefined && (
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('MonthlyDrives')}</label>
						<p className="text-sm font-medium">{planAData.monthly_drives}</p>
					</div>
				)}
				{planAData.platform_score !== undefined && (
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('PlatformScore')}</label>
						<StarRating value={planAData.platform_score} />
					</div>
				)}
				{planAData.completion_rate !== undefined && (
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('CompletionRate')}</label>
						<p className="text-sm font-medium">{planAData.completion_rate}%</p>
					</div>
				)}
			</div>

			{planAData.platform_score_image_url && (
				<div className="mb-4">
					<label className="block text-sm text-gray-500 mb-1">{t('PlatformScoreImage')}</label>
					<button
						type="button"
						onClick={handlePlatformScoreClick}
						disabled={loadingPlatformScore}
						className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1 disabled:text-gray-400"
					>
						{loadingPlatformScore ? t('LoadingImage') : t('ViewImage')} <ExternalLink size={14} />
					</button>
				</div>
			)}

			{planAData.completion_rate_image_url && (
				<div className="mb-4">
					<label className="block text-sm text-gray-500 mb-1">{t('CompletionRateImage')}</label>
					<button
						type="button"
						onClick={handleCompletionRateClick}
						disabled={loadingCompletionRate}
						className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1 disabled:text-gray-400"
					>
						{loadingCompletionRate ? t('LoadingImage') : t('ViewImage')} <ExternalLink size={14} />
					</button>
				</div>
			)}

			{planAData.weekly_summaries && planAData.weekly_summaries.length > 0 && (
				<div>
					<label className="block text-sm text-gray-500 mb-2">{t('WeeklySummaries')}</label>
					<div className="overflow-x-auto">
						<table className="min-w-full border border-gray-200 rounded-lg">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
										{t('Week')}
									</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
										{t('Income')}
									</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
										{t('Rides')}
									</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
										{t('Image')}
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{planAData.weekly_summaries?.map((summary, index) => (
									<tr key={index}>
										<td className="px-4 py-2 text-sm text-gray-900">{summary.week}</td>
										<td className="px-4 py-2 text-sm text-gray-900">
											{formatCurrency(summary.income)}
										</td>
										<td className="px-4 py-2 text-sm text-gray-900">{summary.rides}</td>
										<td className="px-4 py-2 text-sm">
											{summary.image_url ? (
												<button
													type="button"
													onClick={() => handleWeeklySummaryClick(summary.image_url!, index)}
													disabled={loadingWeeklySummaries[index]}
													className="text-blue-500 hover:text-blue-700 flex items-center gap-1 disabled:text-gray-400"
												>
													{loadingWeeklySummaries[index] ? t('LoadingImage') : t('View')}{' '}
													<ExternalLink size={12} />
												</button>
											) : (
												<span className="text-gray-400">-</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};

export default PlanADataSection;
